/**
 * Jyotish Tarang v2.3 — ActivityLog
 * User activity audit panel — shown inside AdminPage audit tab
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

interface LogRow {
  id: number;
  created_at: string;
  user_id: number;
  user_name: string;
  user_email: string;
  tier: string;
  action: string;
  feature: string | null;
  input_summary: string | null;
  status: string;
  duration_ms: number;
}

interface Summary {
  total: number;
  ai_questions: number;
  charts: number;
  pdfs: number;
  predictions: number;
  success: number;
  errors: number;
  quota_exceeded: number;
  success_rate: number;
}

interface UserDetail {
  user: { id: number; name: string; email: string; subscription_tier: string };
  stats: Summary;
  events: Array<{
    action: string;
    feature: string | null;
    input_summary: string | null;
    status: string;
    duration_ms: number;
    created_at: string;
  }>;
}

const STATUS_COLOR: Record<string, string> = {
  success:        'var(--teal)',
  error:          'var(--red)',
  cached:         'var(--indigo)',
  quota_exceeded: 'var(--sf)',
};

const STATUS_BG: Record<string, string> = {
  success:        '#e6f4ea',
  error:          '#fce8e6',
  cached:         '#e8eaf6',
  quota_exceeded: '#fff3e0',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span style={{
      fontSize: 10, padding: '2px 7px', borderRadius: 99, fontWeight: 600,
      background: STATUS_BG[status] || '#f1f1f1',
      color: STATUS_COLOR[status] || 'var(--ink2)',
    }}>
      {status}
    </span>
  );
}

function MetricBox({ label, val, sub }: { label: string; val: string | number; sub?: string }) {
  return (
    <div style={{
      padding: '14px 18px', background: 'var(--cream)',
      borderRadius: 10, border: '0.5px solid var(--parch2)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--ink3)', textTransform: 'uppercase',
                    letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontFamily: 'var(--font-mono)', fontWeight: 600,
                    color: 'var(--indigo)' }}>{val}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function adminHeaders() {
  const tok = sessionStorage.getItem('admin_session_token') || '';
  const jwt = sessionStorage.getItem('access_token') || '';
  return { Authorization: `Bearer ${jwt}`, 'X-Admin-Session': tok };
}

export default function ActivityLog() {
  const [rows,    setRows]    = useState<LogRow[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [pages,   setPages]   = useState(1);
  const [summary, setSummary] = useState<{ today: Summary; week: Summary } | null>(null);
  const [loading, setLoading] = useState(false);
  const [drilldown, setDrilldown] = useState<UserDetail | null>(null);

  const [filterAction, setFilterAction] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate,   setFilterDate]   = useState('today');
  const [search,       setSearch]       = useState('');

  const PER = 50;

  function dateParams() {
    const today = new Date().toISOString().slice(0, 10);
    if (filterDate === 'today') return `&date_from=${today}&date_to=${today}`;
    if (filterDate === '7d') {
      const from = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
      return `&date_from=${from}&date_to=${today}`;
    }
    if (filterDate === '30d') {
      const from = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);
      return `&date_from=${from}&date_to=${today}`;
    }
    return '';
  }

  const fetchLog = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const q = `page=${p}&per_page=${PER}` +
        (filterAction ? `&action=${filterAction}` : '') +
        (filterStatus ? `&status=${filterStatus}` : '') +
        (search       ? `&q=${encodeURIComponent(search)}` : '') +
        dateParams();
      const r = await axios.get(`/admin/activity-log?${q}`, { headers: adminHeaders() });
      setRows(r.data.rows   || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setPage(p);
    } catch {}
    finally { setLoading(false); }
  }, [filterAction, filterStatus, filterDate, search]);

  const fetchSummary = useCallback(async () => {
    try {
      const r = await axios.get('/admin/activity-log/summary', { headers: adminHeaders() });
      setSummary(r.data);
    } catch {}
  }, []);

  const fetchDrilldown = async (uid: number) => {
    try {
      const r = await axios.get(`/admin/activity-log/${uid}`, { headers: adminHeaders() });
      setDrilldown(r.data);
    } catch {}
  };

  useEffect(() => { fetchSummary(); fetchLog(1); }, []);
  useEffect(() => { fetchLog(1); }, [filterAction, filterStatus, filterDate]);

  function exportCSV() {
    const q = (filterAction ? `&action=${filterAction}` : '') +
              (filterStatus ? `&status=${filterStatus}` : '') +
              dateParams();
    window.open(`/admin/activity-log/export?${q}`, '_blank');
  }

  /* ── Drilldown view ── */
  if (drilldown) {
    const { user, stats, events } = drilldown;
    const initials = user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    return (
      <div>
        <button className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }}
                onClick={() => setDrilldown(null)}>
          ← Back to activity log
        </button>

        {/* User header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 42, height: 42, borderRadius: '50%', background: 'var(--indigo)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 600, flexShrink: 0,
          }}>{initials}</div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>{user.name}</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)' }}>
              {user.subscription_tier} · {user.email}
            </div>
          </div>
        </div>

        {/* Mini metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          <MetricBox label="Total events"  val={stats.total} />
          <MetricBox label="AI questions"  val={stats.ai_questions} />
          <MetricBox label="Charts"        val={stats.charts} />
          <MetricBox label="Success rate"  val={`${stats.success_rate}%`} />
        </div>

        {/* Timeline */}
        <div style={{ borderLeft: '2px solid var(--parch2)', paddingLeft: 20, marginLeft: 8 }}>
          {events.map((ev, i) => (
            <div key={i} style={{ position: 'relative', marginBottom: 18 }}>
              <div style={{
                position: 'absolute', left: -26, top: 5,
                width: 8, height: 8, borderRadius: '50%',
                background: STATUS_COLOR[ev.status] || 'var(--ink3)',
              }} />
              <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                {ev.created_at} · {ev.duration_ms}ms
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                            fontSize: 13, fontWeight: 500, margin: '2px 0' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{ev.action}</span>
                <StatusBadge status={ev.status} />
              </div>
              {ev.feature && (
                <div style={{ fontSize: 12, color: 'var(--ink2)' }}>{ev.feature}</div>
              )}
              {ev.input_summary && (
                <div style={{ fontSize: 11, color: 'var(--ink3)', fontStyle: 'italic',
                              maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap' }}>
                  "{ev.input_summary}"
                </div>
              )}
            </div>
          ))}
          {events.length === 0 && (
            <div style={{ color: 'var(--ink3)', fontSize: 13 }}>No events found.</div>
          )}
        </div>
      </div>
    );
  }

  /* ── Main log view ── */
  const s = summary?.today;

  return (
    <div>
      {/* Summary metrics */}
      {s && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
                      gap: 10, marginBottom: 16 }}>
          <MetricBox label="Events today"   val={s.total}
                     sub={`${s.errors} error${s.errors !== 1 ? 's' : ''}`} />
          <MetricBox label="AI questions"   val={s.ai_questions}
                     sub={`${s.quota_exceeded} quota exceeded`} />
          <MetricBox label="Charts created" val={s.charts} />
          <MetricBox label="Success rate"   val={`${s.success_rate}%`}
                     sub={`${s.errors} failed`} />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap',
                    alignItems: 'center' }}>
        <input className="form-input" style={{ maxWidth: 200, fontSize: 12 }}
               value={search}
               onChange={e => setSearch(e.target.value)}
               onKeyDown={e => e.key === 'Enter' && fetchLog(1)}
               placeholder="Search user or email…" />

        <select className="form-input" style={{ fontSize: 12, padding: '6px 10px' }}
                value={filterAction} onChange={e => setFilterAction(e.target.value)}>
          <option value="">All actions</option>
          <option value="chart_created">chart_created</option>
          <option value="prediction_generated">prediction_generated</option>
          <option value="ai_question_asked">ai_question_asked</option>
          <option value="pdf_exported">pdf_exported</option>
          <option value="feature_viewed">feature_viewed</option>
          <option value="login">login</option>
        </select>

        <select className="form-input" style={{ fontSize: 12, padding: '6px 10px' }}
                value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="success">success</option>
          <option value="error">error</option>
          <option value="cached">cached</option>
          <option value="quota_exceeded">quota_exceeded</option>
        </select>

        <select className="form-input" style={{ fontSize: 12, padding: '6px 10px' }}
                value={filterDate} onChange={e => setFilterDate(e.target.value)}>
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="">All time</option>
        </select>

        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                onClick={exportCSV}>
          ↓ Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12,
                        tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ background: 'var(--indigo)', color: '#fff' }}>
              {[
                ['Date & time', '140px'], ['User', '160px'], ['Action', '170px'],
                ['Feature', '130px'], ['Status', '100px'], ['Duration', '80px'],
              ].map(([h, w]) => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left',
                                     fontSize: 11, fontWeight: 500, width: w }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} style={{ padding: '24px', textAlign: 'center',
                                          color: 'var(--ink3)', fontSize: 13 }}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '24px', textAlign: 'center',
                                          color: 'var(--ink3)', fontSize: 13 }}>
                  No events found for selected filters.
                </td>
              </tr>
            )}
            {!loading && rows.map((r, i) => {
              const initials = r.user_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
              return (
                <tr key={r.id}
                    style={{
                      background: i % 2 === 0 ? 'var(--cream)' : 'var(--ivory)',
                      borderBottom: '0.5px solid var(--parch2)',
                      cursor: 'pointer',
                    }}
                    onClick={() => fetchDrilldown(r.user_id)}>
                  <td style={{ padding: '7px 10px', color: 'var(--ink3)', fontSize: 11 }}>
                    {r.created_at}
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: 'var(--indigo)', color: '#fff',
                        fontSize: 9, fontWeight: 600, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>{initials}</div>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                                     whiteSpace: 'nowrap', fontSize: 12 }}>
                        {r.user_name}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 11,
                      background: 'var(--parch)', padding: '2px 6px', borderRadius: 4,
                      color: 'var(--sf)',
                    }}>
                      {r.action}
                    </span>
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--ink2)', fontSize: 11,
                                overflow: 'hidden', textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap' }}>
                    {r.feature || '—'}
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    <StatusBadge status={r.status} />
                  </td>
                  <td style={{ padding: '7px 10px', color: 'var(--ink3)', fontSize: 11,
                                fontFamily: 'var(--font-mono)' }}>
                    {r.duration_ms}ms
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginTop: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
          {total > 0
            ? `${(page - 1) * PER + 1}–${Math.min(page * PER, total)} of ${total} events`
            : ''}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm"
                  disabled={page <= 1}
                  onClick={() => fetchLog(page - 1)}>
            ← Prev
          </button>
          <button className="btn btn-ghost btn-sm"
                  disabled={page >= pages}
                  onClick={() => fetchLog(page + 1)}>
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
